import type { CategoryDTO } from "../api/client";
import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, TrashIcon, FolderIcon, CornerDownRight } from "lucide-react";

// Helper to extract multilingual names
function nameLo(row: CategoryDTO): string {
  return (row.name as { lo?: string })?.lo ?? "—";
}

function nameTh(row: CategoryDTO): string {
  return (row.name as { th?: string })?.th ?? "—";
}

function nameEn(row: CategoryDTO): string {
  return (row.name as { en?: string })?.en ?? "—";
}

// Extended CategoryDTO with hierarchy metadata
type CategoryWithDepth = CategoryDTO & {
  depth: number;
  children?: CategoryWithDepth[];
};

// Build hierarchical tree structure from flat array
function buildCategoryTree(categories: CategoryDTO[]): CategoryWithDepth[] {
  const categoryMap = new Map<string, CategoryWithDepth>();
  const rootCategories: CategoryWithDepth[] = [];

  // First pass: create map with depth initialized
  categories.forEach((cat) => {
    categoryMap.set(cat.id, { ...cat, depth: 0, children: [] });
  });

  // Second pass: build tree structure
  categories.forEach((cat) => {
    const categoryWithDepth = categoryMap.get(cat.id)!;
    if (!cat.parentId) {
      rootCategories.push(categoryWithDepth);
    } else {
      const parent = categoryMap.get(cat.parentId);
      if (parent) {
        categoryWithDepth.depth = parent.depth + 1;
        parent.children = parent.children || [];
        parent.children.push(categoryWithDepth);
      } else {
        // Parent not found, treat as root
        rootCategories.push(categoryWithDepth);
      }
    }
  });

  return rootCategories;
}

// Flatten tree back to array for table display (preserving hierarchy order)
function flattenTree(tree: CategoryWithDepth[]): CategoryWithDepth[] {
  const result: CategoryWithDepth[] = [];
  
  function traverse(nodes: CategoryWithDepth[]) {
    nodes.forEach((node) => {
      result.push(node);
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  }
  
  traverse(tree);
  return result;
}

type CategoriesTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: CategoryDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit: (row: CategoryDTO) => void;
  onDelete: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  getCategoryLabel?: (id: string | null) => string;
};

export function CategoriesTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onDelete,
  onPaginationChange,
  onSortingChange,
  getCategoryLabel = (id) => id ?? "—",
}: CategoriesTableProps) {
  // Build hierarchical tree and flatten for display
  const tree = buildCategoryTree(data);
  const hierarchicalData = flattenTree(tree);

  const columns: TanstackReactTable.ColumnDef<CategoryWithDepth>[] = [
    {
      id: "name",
      header: "Category Name (LO/TH/EN)",
      cell: ({ row }) => {
        const category = row.original;
        const lo = nameLo(category);
        const th = nameTh(category);
        const en = nameEn(category);
        const isRoot = category.depth === 0;
        const indent = category.depth * 24; // 24px per level

        return (
          <div className="flex items-center gap-2" style={{ paddingLeft: `${indent}px` }}>
            {isRoot ? (
              <FolderIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            ) : (
              <CornerDownRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
            <div className="flex items-center gap-1 flex-wrap">
              <span className="font-medium">{lo}</span>
              {th !== "—" && (
                <>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-muted-foreground text-sm">{th}</span>
                </>
              )}
              {en !== "—" && (
                <>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-muted-foreground text-sm">{en}</span>
                </>
              )}
            </div>
          </div>
        );
      },
      size: 300,
    },
    {
      id: "slug",
      header: "Slug",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">{row.original.slug}</span>
      ),
      size: 150,
    },
    {
      id: "parentId",
      header: "Parent Category",
      cell: ({ row }) => {
        const parentLabel = row.original.parentId 
          ? getCategoryLabel(row.original.parentId)
          : "Root";
        return <span className="text-sm">{parentLabel}</span>;
      },
      size: 140,
    },
    {
      id: "sortOrder",
      header: "Order",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.sortOrder}</span>
      ),
      size: 80,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const id = row.original.id;
        if (!canManage) return null;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: () => onDelete(id),
            variant: "destructive" as const,
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 100,
    },
  ];

  return (
    <div data-tourid="catalog-categories-table">
      <DataTable<CategoryWithDepth, unknown>
        noDataMessage="ບໍ່ພົບຫມວດມູ່"
        isLoading={isLoading}
        columns={columns}
        data={hierarchicalData}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(s) => {
          if (s[0]?.id === "") return;
          onSortingChange(s[0]?.id as string, !!s[0]?.desc);
        }}
      />
    </div>
  );
}
